/**
 * Comments Service
 * API operations for task comments
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  AddCommentRequest,
  UpdateCommentRequest,
} from '@/types/api/requests';
import type { CommentResponseDto } from '@/types/api/responses';

export const commentsService = {
  /**
   * Add a comment to a task.
   *
   * @param taskId - Task ID to add comment to
   * @param data - Comment data containing message content
   * @returns Promise with the created comment
   */
  add: (taskId: string, data: AddCommentRequest) =>
    apiClient.post<{ comment: CommentResponseDto }>(
      API_ENDPOINTS.COMMENTS.BASE(taskId),
      data
    ),

  /**
   * Update an existing comment.
   * Note: Only the comment author can update their own comments.
   *
   * @param taskId - Task ID the comment belongs to
   * @param commentId - Comment ID to update
   * @param data - Updated comment data containing new message content
   * @returns Promise with the updated comment
   */
  update: (taskId: string, commentId: string, data: UpdateCommentRequest) =>
    apiClient.put<{ comment: CommentResponseDto }>(
      API_ENDPOINTS.COMMENTS.BY_ID(taskId, commentId),
      data
    ),

  /**
   * Delete a comment from a task.
   * Note: May return 409 Conflict if comment is locked or cannot be deleted.
   *
   * @param taskId - Task ID the comment belongs to
   * @param commentId - Comment ID to delete
   * @returns Promise that resolves on successful deletion
   */
  delete: (taskId: string, commentId: string) =>
    apiClient.delete(API_ENDPOINTS.COMMENTS.BY_ID(taskId, commentId)),
};
